/**
 * Verification codes handed to the visitor at the end of a run.
 *
 * Shape: `TRK-` + base36 of the score (uppercase, at least 3 characters,
 * left-padded with `0`) + one checksum character.
 *
 * The checksum is deliberately trivial — it only has to stop somebody from
 * inventing a code on the spot at the stand. It is NOT cryptography: anyone
 * who reads this file can forge a valid code, and that is fine for a trade
 * fair game.
 */

const PREFIX = 'TRK-'
const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const MIN_BODY = 3
const MAX_SCORE = 10_000_000

function checksum(body: string): string {
  let sum = 0
  for (let i = 0; i < body.length; i++) {
    // Position-weighted so that transposed characters do not collide.
    sum += body.charCodeAt(i) * (i + 1)
  }
  return ALPHABET[sum % 36]
}

/** Encodes the score into a short human-readable code, e.g. "TRK-9IX7". */
export function makeCode(score: number): string {
  const safe = Number.isFinite(score) ? Math.max(0, Math.min(MAX_SCORE, Math.floor(score))) : 0
  const body = safe.toString(36).toUpperCase().padStart(MIN_BODY, '0')
  return PREFIX + body + checksum(body)
}

/** Returns the encoded score, or null when the code is malformed / checksum fails. */
export function decodeCode(code: string): number | null {
  let text = code
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '')
  if (text.startsWith('TRK')) text = text.slice(3)
  if (text.length < MIN_BODY + 1) return null

  const body = text.slice(0, -1)
  const given = text.slice(-1)
  if (!/^[0-9A-Z]+$/.test(body) || checksum(body) !== given) return null

  const score = parseInt(body, 36)
  if (!Number.isFinite(score) || score < 0) return null
  return score
}
